/** B2B Quote Tenders FAQ content for the logged-in Help FAQ page */
export interface FaqItem {
    question: string;
    answerHtml: string;
}

const TC_LINK =
    '<a href="#" data-toggle="modal" data-target="#t-and-c-modal">terms and conditions</a>';

export const PUBLIC_FAQ_ITEMS: FaqItem[] = [
    {
        question: 'What is B2B Quote Tenders and how does it work?',
        answerHtml: `<p>
B2B Quote Tenders is a Tender alert service. You create a profile (Categories and Locations) and we send you Tender and Contract Award alerts that match your profile, and you can view matching opportunities inside your account. We make finding Tenders quick and easy for you - our Tender sourcing team do the hard work as we access over 2,000 UK Public Sector sources publishing over 300 new Tenders each day.
</p>`
    },
    {
        question: "What's the difference between a free registration and a paid subscription?",
        answerHtml: `<p>
Free registration lets you receive Tender and Contract Award alerts and see an overview of the notice, as well as perform limited searches.
</p>
<p>
Paid subscription unlocks additional functionality such as full Buyer/Supplier details, links to Buyer websites, and documents available to view/download within your account, plus tools like reminders, bookmarks and bid management features.
</p>`
    },
    {
        question: "Why am I receiving alerts but can't view the full Tender details?",
        answerHtml: `<p>
Free registration provides alerts/overviews. To access full Tender details, Buyer information and downloadable documents where available, you'll need to upgrade to a paid subscription.
</p>`
    },
    {
        question: 'How do I update my Tender Alert Profile (Categories and Locations)?',
        answerHtml: `<p>
Log in and click <a href="/tenderAlertProfile">Tender Alerts</a> in the top menu. Your matching Tenders update automatically after changes, but you must click Save Categories and Locations to apply them.
</p>`
    },
    {
        question: "I'm getting too many (or too few) matches, what should I change?",
        answerHtml: `<p>
Usually it's your Categories and Locations. Tighten your selection to reduce volume; broaden it to increase volume. After changes, remember to save your Categories and Locations so your matching Tenders refresh. Our team are always happy to help and have years of experience ensuring you see the right alerts - give us a call and we'll run through them with you.
</p>`
    },
    {
        question: "I'm not receiving email alerts, what should I check first?",
        answerHtml: `<p>
First, confirm you have verified your email address and check junk/spam folders. If you find an email in your junk/spam folder then add us to your safe senders list. If you still don't receive alerts, <a href="/help/contact-us">contact support</a> and we can investigate.
</p>
<p>
If you have an IT team they may be able to help you too. Sometimes our emails are blocked due to additional security rules or software applied to your account.
</p>`
    },
    {
        question: 'How do I verify my email address?',
        answerHtml: `<p>
When you register, you'll receive an email asking you to verify your email address. Use the verification link in that email. If you can't find it, check junk/spam or <a href="/help/contact-us">contact support</a>.
</p>`
    },
    {
        question: "I can't log in, what should I do?",
        answerHtml: `<p>
Use the Forgotten Password feature first. If you still can't access your account (for example, you're stuck at the login screen), <a href="/help/contact-us">contact support</a> and we can help.
</p>`
    },
    {
        question: 'Can you reset my password for me?',
        answerHtml: `<p>
Yes. If you <a href="/help/contact-us">contact support</a> we can help you regain access, and you can then update your password once you're logged in.
</p>`
    },
    {
        question: "How do I find the Buyer's website link for a Tender?",
        answerHtml: `<p>
For paid subscribers, Tender details include the Buyer information and link to the Buyer's website.
</p>`
    },
    {
        question: "Can I download the Buyer's Tender documents from my account?",
        answerHtml: `<p>
Yes. When documents are available, paid subscribers can download Buyer documents directly within the account.
</p>`
    },
    {
        question: 'The Tender has lots of documents, can I download them all at once?',
        answerHtml: `<p>
Where multiple Buyer documents are available, the system offers a ZIP file download to help you access the whole pack more quickly.
</p>`
    },
    {
        question: 'Can I bookmark Tenders so I can find them later?',
        answerHtml: `<p>
Yes. You can bookmark Tenders in your account and they appear in your bookmarked area for quick access later. You can also see team members' bookmarks.
</p>`
    },
    {
        question: 'Can I assign a Tender to a team member (or myself)?',
        answerHtml: `<p>
Yes. You can assign responsibility for a Tender to a team member (or yourself) so it's clear who is leading that opportunity.
</p>`
    },
    {
        question: 'Do you accept Tender submissions or upload my Bid to the Buyer?',
        answerHtml: `<p>
No. B2B Quote does not accept Tender responses. Tender responses must be submitted directly to the Buyer via the Buyer's portal, before the stated deadline.
</p>`
    },
    {
        question: "Why do I need to 'Register Your Interest' on the Buyer's portal?",
        answerHtml: `<p>
Many Buyers require suppliers to register interest in advance before they can submit a response. If you don't register interest, you may not be able to submit your Tender response.
</p>`
    },
    {
        question: 'Where do I find Contract Award information?',
        answerHtml: `<p>
The Awards section shows Contract Awards that match your profile and can be useful for identifying subcontracting opportunities and market activity.
</p>`
    },
    {
        question: "I can't find a Contract Award, what can I do?",
        answerHtml: `<p>
Start by contacting the Buyer for more information. If that doesn't work, you can consider submitting a Freedom of Information (FOI) request using our template where applicable.
</p>`
    },
    {
        question: 'How long does an FOI request take?',
        answerHtml: `<p>
FOI responses are typically provided within 20 working days (the authority may contact you if they need longer).
</p>`
    },
    {
        question: 'Can I add team members to my account?',
        answerHtml: `<p>
Yes. Team access is available on paid subscriptions and you can invite team members so they can receive alerts and collaborate.
</p>`
    },
    {
        question: 'Do team members get their own alerts / can they create bespoke alerts?',
        answerHtml: `<p>
Bespoke Tender Alerts can be created so additional alerts can be emailed on a daily basis to individual team members, and they can be edited or deleted.
</p>`
    },
    {
        question: 'How do I pay for a subscription?',
        answerHtml: `<p>
You can pay with card directly on the website. If you prefer, you can request an invoice, and pay by bank transfer or phone.
</p>`
    },
    {
        question: 'Can you send me an invoice so I can raise a Purchase Order (PO)?',
        answerHtml: `<p>
Yes. If you request payment by bank transfer you can input a PO number. If you need us to add a PO to an invoice, just contact us and we'll generate a new invoice.
</p>`
    },
    {
        question: 'What does B2B Quote cost?',
        answerHtml: `<p>
Pricing is shown on the <a href="/account/subscribe">website pricing pages</a> and depends on the subscription package you choose.
</p>`
    },
    {
        question: 'I received an invoice / renewal notice but I thought auto-renew was off, why?',
        answerHtml: `<p>
You can see the Renewal Type in <a href="/account/subscriptions">Account &gt; Subscriptions</a>.
</p>
<ul>
<li><strong>Auto-renew</strong> will automatically take card payment and provide an invoice.</li>
<li><strong>Annual Invoice</strong> will send you an invoice 28 days before renewal is due.</li>
<li><strong>No Renewal</strong> will stop a new invoice being generated - your account will remain active until your current subscription period ends when it will revert to a free registration account.</li>
</ul>
<p>
If you need help or would not like to renew your subscription <a href="/help/contact-us">contact support</a> and we can confirm the account status and update as required.
</p>`
    },
    {
        question: 'Can I cancel my subscription?',
        answerHtml: `<p>
Yes. Contact us and we can cancel renewal so your account reverts to a free account at the end of your current subscription period.
</p>`
    },
    {
        question: 'Do you offer refunds if I cancel?',
        answerHtml: `<p>
Refund requests are handled case-by-case. Where a refund is agreed, we typically request company bank details to process the refund by bank transfer (BACS).
</p>
<p>
See our ${TC_LINK}.
</p>`
    },
    {
        question: 'How do I stop receiving emails / unsubscribe from alerts or marketing emails?',
        answerHtml: `<p>
You can change these in <a href="/account/communicationPreferences">Account &gt; Communication Preferences</a>. You can select individual email types to stop receiving here.
</p>`
    },
    {
        question: 'How do I book a place on an online Tender training workshop?',
        answerHtml: `<p>
You can book and pay for upcoming courses by navigating to the <a href="/training/upcoming">Training page</a> from the top menu when you are logged into your account. You can also contact us and provide attendee names and email addresses so the team can process the booking and send an invoice.
</p>`
    },
    {
        question: 'When is my place secured?',
        answerHtml: `<p>
A place on the course is secured once payment has been received in full (training bookings are subject to the terms and conditions).
</p>`
    },
    {
        question: 'How can I pay for training?',
        answerHtml: `<p>
Payment can be made by credit/debit card through the website, over the phone, or by bank transfer using the bank details shown on the invoice.
</p>`
    },
    {
        question: 'Do paid subscribers receive a discount on training?',
        answerHtml: `<p>
Yes. As a paid subscriber, you receive a discount off the standard training price.
</p>`
    },
    {
        question: 'How do I join the online Tender training workshop on the day?',
        answerHtml: `<p>
You’ll receive the Zoom attendee link along with the meeting ID and password. Workshops typically start at 9:00am and open at 8:30am for attendees to join.</p>`
    },
    {
        question: 'Will the online Tender training workshop be recorded? ',
        answerHtml: `<p>
No. Training workshops are not recorded and we ask attendees not to record.
</p>`
    },
    {
        question: 'Can I cancel and get a refund?',
        answerHtml: `<p>
Cancellation and refunds are subject to terms and conditions. In some cases refunds are not available close to the course date (for example, cancellations made less than 15 days before the course), but you may be able to reschedule instead.
</p>
<p>
See our ${TC_LINK}.
</p>`
    },
    {
        question: 'Can I reschedule, transfer my place to someone else, or hold it for a future date?',
        answerHtml: `<p>
Yes. Depending on circumstances, the team can reschedule you to a future date. For cancellations, options may include transferring the booking to an alternative attendee, putting it on hold for future training dates, or cancelling and providing a refund (bank details may be required to process refunds).
</p>`
    },
    {
        question: 'Will I receive online Tender training workshop course materials after booking or on the day?',
        answerHtml: `<p>
Yes. For workshops, the Training Department typically sends course documentation and a copy of the presentation the day prior to your training date.
</p>`
    }
];



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/account.management/help/help.faq.data.ts